import { useToast } from '@client/components/hooks/use-toast';
import { TopBannerOnboarding } from '@client/components/TopBannerOnboarding';
import { Button } from '@client/components/ui/button';
import {
  getPhaseProgress,
  PROGRESS_STEPS,
  STEP_VALIDATION,
  useLtcFinancialPlanningNavigation,
} from '@client/hooks/useLtcFinancialPlanningNavigation';
import { cn } from '@client/lib';
import { AppState } from '@client/model/store';
import { Check } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

// Import step components
import BottomNavigation from '@/components/ltc-financial-planning/BottomNavigation';
import { useNavigate } from 'react-router';
import { AccountDetailsStep } from './steps/AccountDetailsStep';
import { AssetsStep } from './steps/AssetsStep';
import { ComparisonStep } from './steps/ComparisonStep';
import { CompleteStep } from './steps/CompleteStep';
import { CoverageAssessmentStep } from './steps/CoverageAssessmentStep';
import { FundingStep } from './steps/FundingStep';
import { PoliciesStep } from './steps/PoliciesStep';
import { PolicyTypesStep } from './steps/PolicyTypesStep';
import { PreferencesStep } from './steps/PreferencesStep';
import { PrioritiesStep } from './steps/PrioritiesStep';
import { StepContentProps } from './types';

const stepComponentMap: Record<LtcFinancialPlanningStep, React.ComponentType<StepContentProps>> = {
  'assets': AssetsStep,
  'priorities': PrioritiesStep,
  'account-details': AccountDetailsStep,
  'coverage-assessment': CoverageAssessmentStep,
  'preferences': PreferencesStep,
  'policy-types': PolicyTypesStep,
  'policies': PoliciesStep,
  'funding': FundingStep,
  'comparison': ComparisonStep,
  'complete': CompleteStep,
};

const NAVIGATION_HIDDEN_STEPS: LtcFinancialPlanningStep[] = ['preferences'];

export function LtcFinancialPlanning({ clientId }: ClientIdProps) {
  const navigation = useLtcFinancialPlanningNavigation();
  const { toast, dismiss } = useToast();
  const [toastId, setToastId] = useState<string | null>(null);
  const navigate = useNavigate();

  const ltcFinancialPlanningState = useSelector(
    (state: { ltcFinancialPlanning: AppState['ltcFinancialPlanning'] }) =>
      state.ltcFinancialPlanning,
  );

  const StepComponent = stepComponentMap[navigation.currentStep];
  const currentProgressStep = navigation.progressBarStep;

  // Get validation for current step
  const currentValidation = STEP_VALIDATION[navigation.currentStep];
  const isStepValid = currentValidation.validate(ltcFinancialPlanningState);

  // Dismiss toast when step becomes valid
  useEffect(() => {
    if (isStepValid && toastId) {
      dismiss(toastId);
      setToastId(null);
    }
  }, [isStepValid, dismiss, toastId]);

  const handleContinue = () => {
    if (!isStepValid) {
      const result = toast({
        title: 'Action Required',
        description: currentValidation.errorMessage,
        variant: 'default',
        className: 'border-yellow-500 bg-yellow-50 text-yellow-800',
      });
      setToastId(result.id);
      return;
    }
    navigation.goNext();
  };

  return (
    <div className="flex min-h-screen flex-col bg-white">
      {/* Header with Logo */}
      <div className="border-b border-gray-200 bg-white px-4 py-4 md:px-8">
        <TopBannerOnboarding
          className="px-2 md:px-0"
          actionButton={
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(`/clients/${clientId}/onboarding/funding-sources`)}
            >
              Switch to Old Financing Flow
            </Button>
          }
        />
      </div>

      {/* Progress Bar */}
      <nav
        aria-label="Progress"
        className="px-4 py-6 md:px-8"
      >
        <ol className="mx-auto flex max-w-4xl items-center justify-between">
          {PROGRESS_STEPS.map((step, index) => {
            const isCompleted = currentProgressStep > step.id;
            const isCurrent = currentProgressStep === step.id;
            const isLast = index === PROGRESS_STEPS.length - 1;

            return (
              <li
                key={step.id}
                className="relative flex flex-1 flex-col items-center"
              >
                {/* Step Circle */}
                <div
                  className={cn(
                    'relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border text-sm font-semibold transition-colors',
                    {
                      'border-primary bg-primary text-white': isCompleted || isCurrent,
                      'border-gray-300 bg-white text-gray-500': !isCompleted && !isCurrent,
                    },
                  )}
                >
                  {isCompleted ? (
                    <Check
                      className="h-5 w-5"
                      strokeWidth={3}
                    />
                  ) : (
                    step.id
                  )}
                </div>

                {/* Step Label */}
                <span
                  className={cn('mt-3 text-nowrap text-center text-xs font-medium md:text-sm', {
                    'text-primary': isCurrent || isCompleted,
                    'text-gray-500': !isCurrent && !isCompleted,
                  })}
                >
                  {step.name}
                </span>

                {/* Connecting Line - progress bar showing completion within phase */}
                {!isLast && (
                  <div className="absolute left-1/2 top-5 h-0.5 w-full">
                    <div className="absolute inset-0 bg-gray-200" />
                    <div
                      className="bg-primary absolute inset-y-0 left-0 transition-[width] duration-300 ease-out"
                      style={{ width: `${getPhaseProgress(step.id, navigation.currentStep)}%` }}
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      </nav>

      {/* Main Content */}
      <main className="flex-1 px-4 pb-32 md:px-8">
        <div className="mx-auto max-w-4xl">
          <StepComponent navigation={navigation} />
        </div>
      </main>

      {!NAVIGATION_HIDDEN_STEPS.includes(navigation.currentStep) && (
        <BottomNavigation
          navigation={navigation}
          onContinue={handleContinue}
          canContinue={isStepValid}
        />
      )}
    </div>
  );
}
